import { FileTextOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import { Button, message, Tag } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import api from "../../../api/api";
import DataTable from "../../../Components/DataTable/DataTable";
import JalaliDateCell, {
  formatJalaliDate,
} from "../../../Components/Leads/JalaliDateCell";
import PaidOrderTag from "../../../Components/Leads/PaidOrderTag";
import LeadActions from "../../../Components/Leads/LeadActions";
import LeadInfoModal from "../../../Components/Leads/LeadInfoModal";
import LeadReportModal, {
  STATUS_OPTIONS,
  STATUS_MAP,
} from "../../../Components/Leads/LeadReportModal";
import OperatorCell from "../../../Components/Leads/OperatorCell";
import FirstReportCell from "../../../Components/Leads/FirstReportCell";
import ScenarioCell from "../../../Components/Leads/ScenarioCell";
import {
  SCENARIO_OPTIONS,
  mergeScenario,
} from "../../../Components/Leads/scenarios";
import { getLeadStatusLabel } from "../../../Util/GetLeadStatusLabel";
import ConvertNumbersToEn from "../../../Util/ConvertNumbersToEn";
import styles from "./RenewalLeads.module.css";

export const ReportStatusesCell = ({ record, onShowReport }) => {
  const statuses = record?.reportStatuses || [];
  const paidOrders =
    record?.hasPaidOrder && Array.isArray(record?.paidOrders)
      ? record.paidOrders
      : [];
  return (
    <div style={{ display: "flex", flexWrap: "nowrap", gap: 4, alignItems: "center" }}>
      <Button
        type="default"
        icon={<FileTextOutlined />}
        onClick={() => onShowReport(record)}
        style={
          record?.reportCount > 0
            ? { backgroundColor: "#4A90D9", color: "#fff", borderColor: "#4A90D9" }
            : undefined
        }
      >
        گزارش
      </Button>
      {statuses.length > 0 && (
        <div style={{ display: "flex", flexWrap: "nowrap", gap: 4 }}>
          {statuses.map((s) => (
            <Tag
              key={s}
              color={STATUS_MAP[s]?.color || "default"}
              style={{ margin: 0 }}
            >
              {STATUS_MAP[s]?.label || s}
            </Tag>
          ))}
        </div>
      )}
      {paidOrders.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "nowrap",
            gap: 4,
            direction: "rtl",
          }}
        >
          {paidOrders.map((order) => (
            <PaidOrderTag key={order.orderId} order={order} />
          ))}
        </div>
      )}
    </div>
  );
};

const RenewalLeads = ({ role }) => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [operators, setOperators] = useState([]);
  const [infoData, setInfoData] = useState(null);
  const [infoLoading, setInfoLoading] = useState(false);

  const reportModalRef = useRef(null);

  const loadData = useCallback((pageNumber = 1, pageSize = 100, filters = {}) => {
    setLoading(true);
    api
      .get("/leads/renewals", { params: { pageNumber, pageSize, ...filters } })
      .then((res) => {
        setData(res.data.leads || []);
        setTotal(res.data.total || res.data.leads?.length || 0);
      })
      .catch((error) => {
        console.error("Failed to load renewal leads:", error);
        message.error("خطا در بارگذاری لیدهای تمدید");
        setData([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, []);

  const loadOperators = useCallback(() => {
    api
      .get("/leads/operators")
      .then((res) => setOperators(res.data || []))
      .catch((error) => console.error("Failed to load operators:", error));
  }, []);

  useEffect(() => {
    loadOperators();
  }, [loadOperators]);

  const updateStatus = useCallback((lead, status) => {
    if (!lead.lead_operators_lead?.[0]?.id) {
      message.warning("ابتدا گزارش تماس را ثبت کنید");
      return;
    }
    api
      .post("/leads/change-status/", {
        id: lead.lead_operators_lead[0].id,
        status,
      })
      .then(() => {
        setData((prev) =>
          prev.map((item) =>
            item.id === lead.id
              ? {
                  ...item,
                  lead_operators_lead: [
                    { ...item.lead_operators_lead[0], status },
                  ],
                }
              : item,
          ),
        );
        message.success("وضعیت با موفقیت تغییر کرد");
      })
      .catch((error) => {
        message.error("خطا در تغییر وضعیت");
        console.error("Failed to update status:", error);
      });
  }, []);

  const showReport = useCallback((lead) => {
    reportModalRef.current?.open(
      lead.id,
      lead.username,
      lead.lead_operators_lead?.[0]?.id,
    );
  }, []);

  const showInfo = useCallback((lead) => {
    if (!lead.id) {
      message.info("اطلاعات بیشتری برای این لید موجود نیست");
      return;
    }
    setInfoLoading(true);
    api
      .get("/leads/info/" + lead.id)
      .then((result) => {
        const data = result.data || {};
        const hasSessions =
          (Array.isArray(data.sessions) && data.sessions.length > 0) ||
          !!data.sessionInfo;
        if (!hasSessions) {
          message.info("اطلاعات بیشتری موجود نیست");
          return;
        }
        setInfoData(data);
      })
      .catch((error) => {
        message.error("خطا در دریافت اطلاعات");
        console.error("Failed to get info:", error);
      })
      .finally(() => setInfoLoading(false));
  }, []);

  const columns = useMemo(
    () => [
      {
        title: "موبایل",
        dataIndex: "username",
        searchable: true,
        render: (username) => (
          <span
            onClick={() => {
              navigator.clipboard.writeText(username);
              message.success("شماره موبایل در کلیپ بورد کپی شد");
            }}
          >
            {username}
          </span>
        ),
      },
      {
        title: "نام",
        dataIndex: "firstname",
        searchable: true,
        render: (firstname) => firstname || "-",
      },
      {
        title: "نام خانوادگی",
        dataIndex: "lastname",
        searchable: true,
        render: (lastname) => lastname || "-",
      },
      {
        title: "محصول خریداری شده",
        dataIndex: "productTitle",
        render: (title) => title || "-",
      },
      {
        title: "تاریخ خرید",
        dataIndex: "payDate",
        sorter: true,
        render: (val) => (
          <span dir="ltr">{formatJalaliDate(val) || "-"}</span>
        ),
      },
      {
        title: "تاریخ ثبت نام",
        dataIndex: "createdAt",
        render: (_, lead) => <JalaliDateCell lead={lead} />,
      },
      {
        title: "وضعیت تماس",
        dataIndex: "callStatus",
        filterMultiple: false,
        filters: [
          { text: "تماس گرفته نشده", value: "not_called" },
          { text: "تماس گرفته شده", value: "called" },
        ],
        render: (_, record) => getLeadStatusLabel(record.lead_operators_lead),
      },
      {
        title: "اپراتور",
        dataIndex: "operatorId",
        filterMultiple: false,
        filters: operators.map((op) => ({
          text:
            `${op.firstname || ""} ${op.lastname || ""}`.trim() || op.username,
          value: op.id,
        })),
        render: (_, record) => (
          <OperatorCell lead_operators_lead={record.lead_operators_lead} />
        ),
      },
      {
        title: "اولین گزارش",
        dataIndex: "firstReportDate",
        width: 160,
        render: (_, record) => <FirstReportCell record={record} />,
      },
      {
        title: "گزارشات",
        dataIndex: "reportStatuses",
        width: 240,
        filters: [
          ...STATUS_OPTIONS.map((s) => ({ text: s.label, value: s.value })),
          { text: "تگ مبلغ خریداری شده", value: "__paid__" },
        ],
        render: (_, record) => (
          <ReportStatusesCell record={record} onShowReport={showReport} />
        ),
      },
      {
        title: "سناریو",
        dataIndex: "reportScenarios",
        width: 120,
        filters: SCENARIO_OPTIONS.map((s) => ({
          text: s.label,
          value: s.value,
        })),
        render: (_, record) => <ScenarioCell record={record} />,
      },
      {
        title: "عملیات",
        width: 130,
        render: (_, record) => (
          <LeadActions
            record={record}
            onShowInfo={showInfo}
            onUpdateStatus={updateStatus}
          />
        ),
      },
    ],
    [operators, updateStatus, showReport, showInfo],
  );

  const filtersBeautify = (fData = {}) => {
    const reportStatusValues = (fData.reportStatuses ?? []).filter(
      (v) => v !== "__paid__",
    );
    const hasPaidOrder = (fData.reportStatuses ?? []).includes("__paid__");
    return {
      ...(fData.username?.at(0)
        ? { mobile: ConvertNumbersToEn(fData.username.at(0)) }
        : {}),
      ...(fData.firstname?.at(0) ? { name: fData.firstname.at(0) } : {}),
      ...(fData.lastname?.at(0) ? { family: fData.lastname.at(0) } : {}),
      ...(fData.callStatus?.at(0)
        ? { callStatus: fData.callStatus.at(0) }
        : {}),
      ...(fData.operatorId?.at(0)
        ? { operatorId: fData.operatorId.at(0) }
        : {}),
      ...(reportStatusValues.length
        ? { reportStatuses: reportStatusValues.join(",") }
        : {}),
      ...(fData.reportScenarios?.length
        ? { scenarios: fData.reportScenarios.join(",") }
        : {}),
      ...(hasPaidOrder ? { hasPaidOrder: "true" } : {}),
    };
  };

  return (
    <>
      <DataTable
        tableKey="renewal-leads"
        dynamicPagination
        serverSort
        showRowNumber
        recordsCount={total}
        filtersBeautify={filtersBeautify}
        header={{
          title: "لیدهای نقشه دوم",
          avatar: { icon: <UsergroupAddOutlined />, size: "large" },
        }}
        userRole={role}
        onReset={loadData}
        columns={columns}
        dataSource={data}
        hideComment
        hideErrorReport
        setDataSource={setData}
        reload={loadData}
        path="/leads/renewals"
        permissionsPrefix="leads_renewal"
        loading={loading}
      />

      <LeadReportModal
        ref={reportModalRef}
        disableNewReport
        onReportChange={(leadId, reportDate, scenario) => {
          setData((prev) =>
            prev.map((item) =>
              item.id === leadId
                ? {
                    ...item,
                    reportCount: (item.reportCount || 0) + 1,
                    firstReportDate:
                      item.firstReportDate ||
                      (reportDate
                        ? new Date(reportDate).toISOString()
                        : item.firstReportDate),
                    reportScenarios: mergeScenario(
                      item.reportScenarios,
                      scenario,
                    ),
                  }
                : item,
            ),
          );
        }}
      />

      <LeadInfoModal
        info={infoData}
        loading={infoLoading}
        onClose={() => setInfoData(null)}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  permissions: state.userData?.permissions || null,
  role: state.userData?.role || null,
});

export default connect(mapStateToProps)(RenewalLeads);
